import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  fruitSelectionLimits,
  fruitSelectionOptions,
  getProduct,
} from "../data";
import { useApp } from "../store";
import { PageHeader, PrimaryButton } from "../components/UI";

import "../css/ProductDetails.css";

// Approximate weight (grams) for one typical whole fruit. These values are
// only used to estimate the mix shown to customers; actual market weights vary.
const FRUIT_REFERENCE_WEIGHTS = {
  Banana: 300,
  Apple: 350,
  Orange: 350,
  Guava: 300,
  Papaya: 700,
  Pomegranate: 250,
  Watermelon: 1000,
  Pineapple: 1000,
  Grapes: 350,
};

function getBasketWeightGrams(weightLabel) {
  const match = String(weightLabel || "").match(/([\d.]+)\s*kg/i);
  return match ? Math.round(Number(match[1]) * 1000) : 2000;
}

function getFruitWeightEstimate(selectedFoods, targetWeightGrams) {
  const referenceTotal = selectedFoods.reduce(
    (total, fruit) => total + (FRUIT_REFERENCE_WEIGHTS[fruit] || 300),
    0,
  );

  // Do not suggest cutting or fractional fruit. If one typical whole piece of
  // each selected fruit already exceeds the basket limit, ask the customer to
  // change their selection instead of shrinking those estimates unrealistically.
  if (referenceTotal > targetWeightGrams) {
    return { exceedsLimit: true, items: [], referenceTotal };
  }

  // Allocate the remaining basket weight proportionally. This also means that
  // removing a fruit instantly redistributes its weight across the remaining types.
  const items = selectedFoods.map((fruit) => ({
    fruit,
    grams: Math.round(
      (FRUIT_REFERENCE_WEIGHTS[fruit] || 300) *
        (targetWeightGrams / Math.max(referenceTotal, 1)),
    ),
  }));

  // Correct rounding on the last fruit so the displayed estimates add up exactly.
  if (items.length) {
    const difference =
      targetWeightGrams - items.reduce((sum, item) => sum + item.grams, 0);
    items[items.length - 1].grams += difference;
  }
  return { exceedsLimit: false, items, referenceTotal };
}

function formatFruitWeight(grams) {
  return grams >= 1000
    ? `${Number((grams / 1000).toFixed(2))} kg`
    : `${grams} g`;
}

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { draft, setDraft } = useApp();
  const product = getProduct(id);
  const isFruit = product?.type === "fruit_basket";
  const selectionLimit = fruitSelectionLimits[id] || 7;
  const existingSelection =
    draft?.productId === id && Array.isArray(draft?.selectedFoods)
      ? draft.selectedFoods
      : [];
  const [selectedFoods, setSelectedFoods] = useState(() =>
    existingSelection.length
      ? existingSelection
      : isFruit
        ? [fruitSelectionOptions[0]]
        : [],
  );
  const basketTargetGrams = isFruit ? getBasketWeightGrams(product?.weight) : 0;
  const fruitWeightEstimate = isFruit
    ? getFruitWeightEstimate(selectedFoods, basketTargetGrams)
    : null;
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!isFruit) return;
    setSelectedFoods(
      existingSelection.length ? existingSelection : [fruitSelectionOptions[0]],
    );
    // Reset only when the basket product changes; existing choices remain intact.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!product) {
    return (
      <div className="page-wrap container-xl">
        <PageHeader title="Product not found" />
        <Link to="/home" className="btn btn-brand">
          Back home
        </Link>
      </div>
    );
  }

  const toggleFood = (food) => {
    setSelectedFoods((current) => {
      if (current.includes(food)) {
        return current.length > 1
          ? current.filter((item) => item !== food)
          : current;
      }
      if (current.length >= selectionLimit) return current;
      return [...current, food];
    });
  };
  const startSchedule = () => {
    if (
      isFruit &&
      (selectedFoods.length < 1 || fruitWeightEstimate?.exceedsLimit)
    )
      return;
    setDraft({
      ...(draft || {}),
      productId: product.id,
      type: product.type,
      ...(isFruit ? { selectedFoods } : {}),
    });
    navigate(`/schedule/${product.type}/${product.id}`);
  };

  return (
    <div className="page-wrap container-xl">
      <PageHeader
        eyebrow={isFruit ? "Seasonal basket" : "Natural shake"}
        title={product.name}
        copy={product.description}
      />
      <div className="product-detail">
        <div className="product-detail-image">
          <img src={product.image} alt={product.name} />
        </div>
        <div className="product-detail-copy pt-2">
          <div className="price-large">
            ₹{product.price}
            <span> / delivery</span>
          </div>
          {product.protein && (
            <div className="nutrition-grid">
              <div>
                <strong>{product.protein}</strong>
                <span>Protein*</span>
              </div>
              <div>
                <strong>{product.calories}</strong>
                <span>Calories*</span>
              </div>
              <div>
                <strong>{product.fat}</strong>
                <span>Fat*</span>
              </div>
              <div>
                <strong>{product.servingSize}</strong>
                <span>Serving Size*</span>
              </div>
            </div>
          )}
          {product.weight && (
            <div className="nutrition-row">
              <span>
                <i className="fa-solid fa-weight-scale" /> {product.weight}
              </span>
            </div>
          )}
          {isFruit ? (
            <section
              className="fruit-picker mt-4"
              aria-labelledby="fruit-picker-title"
            >
              <div className="fruit-picker-heading">
                <h3 id="fruit-picker-title">Build your fruit basket</h3>
                <span>
                  {selectedFoods.length} of {selectionLimit} selected
                </span>
              </div>
              <p className="fruit-picker-help">
                Choose at least 1 fruit. Select up to {selectionLimit} different
                types for this basket.
              </p>
              <div className="fruit-picker-options">
                {fruitSelectionOptions.map((food) => {
                  const checked = selectedFoods.includes(food);
                  const limitReached = selectedFoods.length >= selectionLimit;
                  return (
                    <label
                      className={`fruit-option${checked ? " selected" : ""}`}
                      key={food}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!checked && limitReached}
                        onChange={() => toggleFood(food)}
                      />
                      <span>{food}</span>
                      {checked && (
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      )}
                    </label>
                  );
                })}
              </div>
              {fruitWeightEstimate?.exceedsLimit ? (
                <div className="fruit-weight-alert" role="alert">
                  Your selected fruits exceed the{" "}
                  {formatFruitWeight(basketTargetGrams)} basket limit. Reduce
                  the number of fruits or replace a heavy fruit with another
                  option.
                </div>
              ) : (
                <div className="fruit-weight-estimate" aria-live="polite">
                  <div className="fruit-weight-estimate-heading">
                    <strong>Approximate fruit quantities</strong>
                    <span>{formatFruitWeight(basketTargetGrams)} total</span>
                  </div>
                  <p>
                    Whole fruits only — no fruit is cut. Estimated weights
                    adjust automatically when you change your selection.
                  </p>
                  <ul>
                    {fruitWeightEstimate?.items.map(({ fruit, grams }) => (
                      <li key={fruit}>
                        <span>{fruit}</span>
                        <strong>{formatFruitWeight(grams)}</strong>
                      </li>
                    ))}
                  </ul>
                  <small>
                    Weights are estimates; actual market fruit sizes may vary.
                  </small>
                </div>
              )}
              {selectedFoods.length === 1 && (
                <small className="fruit-picker-help">
                  At least one fruit must stay selected.
                </small>
              )}
            </section>
          ) : (
            <>
              <h3 className="mt-4">What's inside?</h3>
              <div className="ingredient-list">
                {product.ingredients.map((item) => (
                  <span key={item}>
                    <i className="fa-solid fa-check" />
                    {item}
                  </span>
                ))}
              </div>
            </>
          )}
          <div className="freshness-banner mt-4">
            <i className="fa-solid fa-leaf" />
            <div>
              <strong>Prepared fresh</strong>
              <p>
                {isFruit
                  ? "We source according to your scheduled demand."
                  : "Your shake is prepared fresh for your scheduled delivery."}
              </p>
            </div>
          </div>
          {isFruit ? (
            <div className="fruit-basket-tc mt-3">
              <strong className="mt-2">Note:</strong>
              <p>
                We buy whole fruits fresh for your scheduled delivery. The fruit
                weights shown above are approximate and may vary by market size.
              </p>
            </div>
          ) : (
            ""
          )}
          <PrimaryButton
            onClick={startSchedule}
            className="w-100 btn-lg mt-4"
            icon="fa-arrow-right"
            disabled={
              isFruit &&
              (selectedFoods.length < 1 || fruitWeightEstimate?.exceedsLimit)
            }
          >
            Schedule delivery
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}
